import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import { forgetSignIn, rememberMicrosoft, rememberedSignIn } from './signInMemory';

/**
 * Staff sign-in against Microsoft Entra.
 *
 * NCMB staff live in NCMB's own directory, which this app does not administer,
 * so the authority is /organizations rather than a specific tenant and the
 * server decides whether the directory the token came from is the right one.
 * Nothing here is a secret: the client id ships in the bundle by design,
 * because a browser cannot keep one. Authentication rests on the redirect URI
 * being registered and on PKCE, not on hiding an identifier.
 *
 * STAYING SIGNED IN BETWEEN LAUNCHES. Gordon: "when I click on the desktop app
 * it is still asking me whether I use the Microsoft login or PIN even if I
 * have accessed Jarvis and closed it off multiple times per day."
 *
 * Three things made every launch a stranger:
 *
 *   1. MSAL's cache was in sessionStorage, which belongs to one window. The
 *      installed app opens a new window every time, so it always started
 *      empty. It is in localStorage now. MSAL encrypts what it keeps there
 *      with a key held in a SESSION cookie, so the tokens still become
 *      unreadable when the browser itself is closed (and a shared computer
 *      is no worse off than before); what changes is that a second window,
 *      or the app closed and opened again while the browser runs, finds them.
 *
 *   2. Nothing tried to sign anybody back in. Now, when MSAL has no account
 *      but this device remembers who last came in with Microsoft
 *      (lib/signInMemory - a login hint, never a token), initAuth asks
 *      Microsoft quietly first: ssoSilent with that hint, in a hidden frame,
 *      on the strength of the Microsoft session the browser already has
 *      (Windows sign-in, or "stay signed in"). Only if that needs a person
 *      does the front door show - and then as "Continue as Rita", one press.
 *
 *   3. That quiet sign-in could never have worked. MSAL 5 hears the answer
 *      from the hidden frame through a "redirect bridge": the page the frame
 *      lands on must hand the answer back to the app. Ours booted the whole
 *      app instead, so the frame said nothing and the attempt timed out -
 *      as did every silent token renewal once the refresh token was spent.
 *      index.js now runs the bridge, and only the bridge, in that frame. The
 *      frame lands on the redirect URI already registered, so Entra needs
 *      nothing new.
 *
 * Signing out on purpose forgets the remembered method (and MSAL's account),
 * so the next person at the machine gets the plain choice.
 */

const CLIENT_ID = process.env.REACT_APP_AZURE_CLIENT_ID;
const AUTHORITY = process.env.REACT_APP_AZURE_AUTHORITY
  || 'https://login.microsoftonline.com/organizations';
const REDIRECT_URI = process.env.REACT_APP_REDIRECT_URI || window.location.origin;

// A token minted for this API, not the identity token. An identity token proves
// who someone is; it is not a key to this application, and Microsoft advises
// against using one as though it were. Falls back to the client's own id when
// the API scope has not been exposed on the registration yet.
const API_SCOPE = process.env.REACT_APP_API_SCOPE
  || (CLIENT_ID ? `api://${CLIENT_ID}/access_as_user` : null);
const SCOPES = API_SCOPE ? [API_SCOPE] : ['openid', 'profile', 'email'];

// How long the quiet sign-in may take before the front door is shown instead.
// Microsoft answers in well under a second when it can; the wait only runs
// out when something blocks the frame altogether.
const SILENT_MS = 6000;

export const authConfigured = Boolean(CLIENT_ID);

let msal = null;
let readyPromise = null;
let silent = 'idle';   // idle | trying | signed-in | needs-you

function instance() {
  if (!authConfigured) return null;
  if (!msal) {
    msal = new PublicClientApplication({
      auth: { clientId: CLIENT_ID, authority: AUTHORITY, redirectUri: REDIRECT_URI },
      cache: {
        // localStorage, so the next window finds the sign-in; MSAL encrypts it
        // with a browser-session key, so closing the browser still ends it.
        // See the note at the top.
        cacheLocation: 'localStorage'
      },
      system: { iframeBridgeTimeout: SILENT_MS }
    });
  }
  return msal;
}

/** In MSAL's own hidden frame (or a popup's): nothing to sign in from there. */
const inAFrame = () => {
  try { return window.parent !== window; } catch { return true; }
};

/**
 * Must finish before any other call. Handles the return leg of a redirect,
 * and - for somebody this device remembers - the quiet sign-in.
 */
export function initAuth() {
  if (!authConfigured) return Promise.resolve(null);
  if (!readyPromise) {
    readyPromise = (async () => {
      const app = instance();
      await app.initialize();
      const result = await app.handleRedirectPromise();
      let account = result?.account || app.getActiveAccount() || app.getAllAccounts()[0] || null;
      const memo = rememberedSignIn();
      if (!account && memo?.method === 'microsoft' && !inAFrame()) {
        silent = 'trying';
        try {
          const r = await app.ssoSilent({ scopes: SCOPES, loginHint: memo.hint });
          account = r?.account || null;
        } catch {
          // Microsoft wants the person (signed out there, a second account,
          // consent, third-party cookies blocked...). Not an error: the front
          // door offers "Continue as <name>" instead.
          account = null;
        }
        silent = account ? 'signed-in' : 'needs-you';
      }
      if (account) {
        app.setActiveAccount(account);
        rememberMicrosoft(account);
      }
      return app.getActiveAccount();
    })();
  }
  return readyPromise;
}

/** How the quiet sign-in went: 'idle' (not tried), 'trying', 'signed-in' or 'needs-you'. */
export function silentSignIn() { return silent; }

export function currentAccount() {
  const app = instance();
  return app ? app.getActiveAccount() : null;
}

/**
 * Sign in with Microsoft. `hint` (an address) goes to Microsoft as the login
 * hint, so "Continue as Rita" lands on Rita rather than an account picker;
 * `pickAccount` asks Microsoft to offer the choice instead ("Use a different
 * Microsoft account").
 */
export async function signIn({ hint = null, pickAccount = false } = {}) {
  const app = instance();
  if (!app) throw new Error('Sign-in is not configured');
  await initAuth();
  // Redirect rather than popup: popups are blocked often enough that the
  // failure looks like the button not working.
  return app.loginRedirect({
    scopes: SCOPES,
    ...(hint && !pickAccount ? { loginHint: hint } : {}),
    ...(pickAccount ? { prompt: 'select_account' } : {})
  });
}

export async function signOut() {
  // Forgotten first: whatever Microsoft's page does next, this device no
  // longer offers "Continue as" for whoever was here.
  forgetSignIn();
  const app = instance();
  if (!app) return;
  await initAuth();
  return app.logoutRedirect({ postLogoutRedirectUri: REDIRECT_URI, account: app.getActiveAccount() || undefined });
}

/**
 * A token for the API, refreshed silently when it can be and interactively when
 * it cannot. Returns null when nobody is signed in, so callers can stay
 * anonymous rather than being bounced.
 */
export async function getApiToken() {
  const app = instance();
  if (!app) return null;
  await initAuth();
  const account = app.getActiveAccount();
  if (!account) return null;

  const request = { account, scopes: SCOPES };

  try {
    const r = await app.acquireTokenSilent(request);
    // With an API scope there is a real access token; without one the id token
    // is all Entra will issue, and the server accepts either audience.
    return r.accessToken || r.idToken || null;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError && !inAFrame()) {
      await app.acquireTokenRedirect(request);
      return null;
    }
    console.error('Could not get an access token:', err);
    return null;
  }
}
